/**
 * FusionTimeline — staged reveal of the case graph on /fusion.
 *
 * Collapsed: a right-side chip with a stage-colored dot + "Ingestion Timeline"
 * label (same discoverability pattern as the collapsed Inspector strip).
 * Expanded: full bottom scrubber. Play advances stages (bank → crypto → OSINT →
 * company). Not transfer playback; not the Run rail.
 */
import { ChevronDown } from 'lucide-react';
import { TimelineStepperControls } from '../TimelineStepperControls';
import { useFusionStore } from '../../stores/useFusionStore';
import { INGEST_STEPS, MAX_STEP, USER_INGEST_STEPS, USER_MAX_STEP } from '../../data/fusionSeed';
import { MODULE_COLOR, type FusionModule } from '../../types/fusion';

const labelsFor = (steps: typeof INGEST_STEPS) => ({
  labels: steps.map((s) => s.title),
  subs: steps.map((s) => s.sub),
  accents: steps.map((s) => MODULE_COLOR[s.module as FusionModule]),
});
const DEMO = labelsFor(INGEST_STEPS);
const USER = labelsFor(USER_INGEST_STEPS);

export function FusionTimeline() {
  const step = useFusionStore((s) => s.step);
  const playing = useFusionStore((s) => s.playing);
  const showDemo = useFusionStore((s) => s.showDemo);
  const timelineExpanded = useFusionStore((s) => s.timelineExpanded);
  const setStep = useFusionStore((s) => s.setStep);
  const setPlaying = useFusionStore((s) => s.setPlaying);
  const setTimelineExpanded = useFusionStore((s) => s.setTimelineExpanded);
  const replay = useFusionStore((s) => s.replay);

  const steps = showDemo ? INGEST_STEPS : USER_INGEST_STEPS;
  const total = showDemo ? MAX_STEP : USER_MAX_STEP;
  const view = showDemo ? DEMO : USER;
  const cur = Math.min(step, total);
  const atEnd = cur >= total;
  const curStep = steps[cur - 1];
  const accent = MODULE_COLOR[(curStep?.module as FusionModule) || 'shared'];

  const onPrimaryPlay = () => {
    if (cur >= total) replay();
    else setPlaying(true);
  };

  if (!timelineExpanded) {
    return (
      <button
        type="button"
        onClick={() => setTimelineExpanded(true)}
        title={`Expand Ingestion Timeline — Stage ${cur} / ${total}${playing ? ' (playing)' : ''}`}
        aria-label="Expand Ingestion Timeline"
        aria-expanded={false}
        data-tour="fusion-timeline"
        data-collapsed="true"
        className="absolute right-3 bottom-3 z-10 flex items-center gap-2 rounded-full border border-border bg-card/95 backdrop-blur shadow-lg pl-2 pr-3 py-2 hover:bg-secondary/80 hover:border-primary/40 transition-colors"
      >
        <span
          className={`relative w-3 h-3 rounded-full shrink-0 ${playing ? 'animate-pulse' : ''}`}
          style={{ background: accent }}
          aria-hidden
        >
          {playing && (
            <span
              className="absolute inset-0 rounded-full animate-ping opacity-40"
              style={{ background: accent }}
            />
          )}
        </span>
        <span className="flex flex-col items-start leading-tight min-w-0">
          <span className="text-[11px] font-semibold text-foreground whitespace-nowrap">
            Ingestion Timeline
          </span>
          <span className="text-[9px] text-muted-foreground tabular-nums whitespace-nowrap">
            Stage {cur} / {total}
            {playing ? ' · playing' : atEnd ? ' · complete' : ''}
          </span>
        </span>
      </button>
    );
  }

  return (
    <div
      className="absolute bottom-3 left-3 right-3 z-10 rounded-xl border border-border bg-card/95 backdrop-blur shadow-lg px-3 py-2"
      data-tour="fusion-timeline"
      data-collapsed="false"
      role="region"
      aria-label="Ingestion timeline"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: accent }}
            aria-hidden
          />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            Ingestion Timeline
          </span>
          <span className="text-[10px] text-muted-foreground truncate">
            {showDemo
              ? 'Play reveals each evidence surface — bank, crypto, OSINT, company'
              : 'Play reveals your data one layer at a time'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] font-medium text-foreground tabular-nums px-1.5 py-0.5 rounded border border-border bg-secondary/60">
            Stage {cur} / {total}
            {playing ? ' · playing' : atEnd ? ' · complete' : ''}
          </span>
          <button
            type="button"
            onClick={() => setTimelineExpanded(false)}
            title="Collapse to Ingestion Timeline dot"
            aria-label="Collapse Ingestion Timeline"
            aria-expanded={true}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <TimelineStepperControls
        currentStep={cur}
        totalSteps={total}
        isPlaying={playing}
        onPlay={onPrimaryPlay}
        onPause={() => setPlaying(false)}
        onRestart={replay}
        onStepChange={(n) => { setPlaying(false); setStep(n); }}
        stepLabels={view.labels}
        stepSubs={view.subs}
        accentColors={view.accents}
        playLabel={playing ? 'Pause' : atEnd ? 'Replay' : 'Play stages'}
      />
    </div>
  );
}
